// Shareable crawl links: ?crawl=w123-n456-w789&t=2026-07-17T17:15Z&m=45
//   crawl  the pubs' OSM ids, in visiting order
//   t      arrival at the first pub (UTC, to the minute)
//   m      minutes at each pub
// The link names the exact pubs, so the recipient sees the same crawl even
// if the data changes (a pub that's gone is reported, not silently dropped).
// The start point isn't in the link: it's often someone's location.

import { DWELL_STEPS, MAX_STOPS } from './config';

export interface CrawlLink {
  ids: string[];
  /** Arrival at the first pub, epoch ms (whole minutes). */
  start: number;
  /** Minutes at each pub. */
  dwell: number;
}

const PUB_ID = /^[nwr]\d{1,12}$/;
const ISO_MINUTE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/;
const MINUTE_MS = 60_000;

/** "2026-07-17T17:15Z" */
export const isoMinute = (ms: number) =>
  `${new Date(Math.floor(ms / MINUTE_MS) * MINUTE_MS).toISOString().slice(0, 16)}Z`;

export function writeCrawlParams(params: URLSearchParams, link: CrawlLink): URLSearchParams {
  params.set('crawl', link.ids.join('-'));
  params.set('t', isoMinute(link.start));
  params.set('m', String(link.dwell));
  return params;
}

/** The crawl in a URL's query string, or null if there isn't a valid one. */
export function readCrawlParams(search: string): CrawlLink | null {
  const params = new URLSearchParams(search);
  const raw = params.get('crawl');
  if (!raw) return null;
  const ids = [...new Set(raw.split(/[-,]/).filter((id) => PUB_ID.test(id)))].slice(0, MAX_STOPS);
  if (ids.length === 0) return null;
  const t = params.get('t') ?? '';
  const ms = ISO_MINUTE.test(t) ? Date.parse(t) : NaN;
  if (!Number.isFinite(ms)) return null;
  const m = Number(params.get('m'));
  const dwell = (DWELL_STEPS as readonly number[]).includes(m) ? m : 45;
  return { ids, start: Math.floor(ms / MINUTE_MS) * MINUTE_MS, dwell };
}

export function crawlShareUrl(origin: string, link: CrawlLink): string {
  const url = new URL('/', origin);
  writeCrawlParams(url.searchParams, link);
  return url.toString();
}

/** Remove a crawl from the address bar (keeping anything else, like filters). */
export function clearCrawlParams(url: URL): URL {
  for (const key of ['crawl', 't', 'm']) url.searchParams.delete(key);
  return url;
}
